import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AiTopicSettingsService, Topic, SubTopic, BatchOperationsPayload } from '../ai-topic-settings.service';
import { UserManagementService } from '../../user-management/user-management.service';
import { MatDialog } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

export interface LocalTopic extends Topic {
  action: 'same' | 'create' | 'update' | 'delete';
  originalData?: Topic;
  tempId?: number;
}

export interface LocalSubTopic extends SubTopic {
  action: 'same' | 'create' | 'update' | 'delete';
  originalData?: SubTopic;
  tempId?: number;
}

@Component({
    selector: 'app-local-topic-manager',
    templateUrl: './local-topic-manager.component.html',
    styleUrls: ['./local-topic-manager.component.css'],
    standalone: false
})
export class LocalTopicManagerComponent implements OnInit, OnChanges {
  
  @Input() triggerSave: boolean = false;
  
  topics: LocalTopic[] = [];
  visibleTopics: LocalTopic[] = [];
  selectedTopic: LocalTopic | null = null;
  selectedSubTopics: LocalSubTopic[] = [];
  visibleSubTopics: LocalSubTopic[] = [];
  loading = false;
  saving = false;
  currentUser: any;
  currentSite: any;
  userId: number = 0;
  siteId: number = 0;
  hasChanges = false;

  // Modal states
  showTopicModal = false;
  showSubTopicModal = false;
  showAddTopicForm = false;
  modalMode: 'add' | 'edit' = 'add';
  editingTopic: LocalTopic | null = null;
  editingSubTopic: LocalSubTopic | null = null;
  selectedTopicForSubtopic: number | null = null;

  // Forms
  topicForm: FormGroup;
  subTopicForm: FormGroup;

  constructor(
    private aiTopicService: AiTopicSettingsService,
    private userService: UserManagementService,
    private dialog: MatDialog,
    private messageService: MessageService,
    private fb: FormBuilder
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.initializeUserData();
    this.loadTopics();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['triggerSave'] && changes['triggerSave'].currentValue === true) {
      this.saveAllChanges();
    }
  }

  private initializeForms(): void {
    this.topicForm = this.fb.group({
      topic_name: ['', [Validators.required, Validators.minLength(2)]],
      status: [true]
    });

    this.subTopicForm = this.fb.group({
      topic_id: [null, [Validators.required]],
      sub_topic_name: ['', [Validators.required, Validators.minLength(2)]],
      secondary_prompt: ['', [Validators.required, Validators.minLength(5)]],
      status: [true]
    });
  }

  private initializeUserData(): void {
    this.currentUser = this.userService.getUserDetail();
    this.currentSite = this.userService.getSiteDetail();
    
    if (this.currentUser) {
      this.userId = this.currentUser.id ? +this.currentUser.id : 0;
    }
    
    if (this.currentSite && this.currentSite.length > 0) {
      this.siteId = this.currentSite[0].site_id || 0;
    }
  }

  private loadTopics(): void {
    this.loading = true;
    this.aiTopicService.getTopicsWithSubtopics(this.userId, this.siteId).subscribe({
      next: (response) => {
        if (response.success) {
          // Initialize topics with local action tracking
          this.topics = response.data.map(topic => ({
            ...topic,
            action: 'same' as const,
            originalData: { ...topic },
            sub_topics: topic.sub_topics?.map(subTopic => ({
              ...subTopic,
              action: 'same' as const,
              originalData: { ...subTopic }
            })) || []
          }));
          
          if (this.topics.length > 0) {
            this.selectedTopic = this.topics[0];
            this.selectedSubTopics = this.selectedTopic.sub_topics as LocalSubTopic[] || [];
          } else {
            this.copySystemTopics();
          }

          this.refreshVisibleLists();
        } else {
          this.showError('Failed to load topics: ' + response.message);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading topics:', error);
        this.showError('Error loading topics. Please try again.');
        this.loading = false;
      }
    });
  }

  private copySystemTopics(): void {
    this.loading = true;
    this.aiTopicService.copySystemTopics(this.siteId).subscribe({
      next: (response) => {
        if (response.success) {
          this.showSuccess('System topics copied successfully');
          this.loadTopics();
        } else {
          this.showError('Failed to copy system topics: ' + response.message);
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error copying system topics:', error);
        this.showError('Error copying system topics. Please try again.');
        this.loading = false;
      }
    });
  }

  onTopicChange(topic: LocalTopic): void {
    this.selectedTopic = topic;
    this.selectedSubTopics = topic.sub_topics as LocalSubTopic[] || [];
    this.refreshVisibleLists();
  }

  // Topic CRUD Operations
  openAddTopicModal(): void {
    this.showAddTopicForm = false;
    this.editingTopic = null;
    this.topicForm.reset();
    this.showTopicModal = true;
  }

  showAddTopicFormMethod(): void {
    this.showAddTopicForm = true;
    this.editingTopic = null;
    this.topicForm.reset();
    this.topicForm.patchValue({ status: true });
  }

  editTopic(topic: LocalTopic): void {
    this.editingTopic = topic;
    this.showAddTopicForm = true;
    this.topicForm.patchValue({
      topic_name: topic.topic_name,
      status: topic.status
    });
  }

  cancelEdit(): void {
    this.showAddTopicForm = false;
    this.editingTopic = null;
    this.topicForm.reset();
  }

  closeTopicModal(): void {
    this.showTopicModal = false;
    this.showAddTopicForm = false;
    this.editingTopic = null;
    this.topicForm.reset();
  }

  saveTopicModal(): void {
    if (this.topicForm.valid) {
      const formData = this.topicForm.value;
      
      if (this.editingTopic) {
        this.updateTopicLocally(this.editingTopic, formData);
      } else {
        this.addTopicLocally(formData);
      }
      
      this.showAddTopicForm = false;
      this.editingTopic = null;
      this.topicForm.reset();
    } else {
      this.markFormGroupTouched(this.topicForm);
    }
  }

  deleteTopic(topic: LocalTopic): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete the topic "${topic.topic_name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteTopicLocally(topic);
      }
    });
  }

  // SubTopic CRUD Operations
  openAddSubTopicModal(): void {
    this.modalMode = 'add';
    this.editingSubTopic = null;
    this.subTopicForm.reset();
    this.subTopicForm.patchValue({ 
      topic_id: this.selectedTopic?.topic_id || null,
      status: true 
    });
    this.showSubTopicModal = true;
  }

  editSubTopic(subTopic: LocalSubTopic): void {
    this.modalMode = 'edit';
    this.editingSubTopic = subTopic;
    this.subTopicForm.patchValue({
      topic_id: subTopic.topic_id,
      sub_topic_name: subTopic.sub_topic_name,
      secondary_prompt: subTopic.secondary_prompt,
      status: subTopic.status
    });
    this.showSubTopicModal = true;
  }

  closeSubTopicModal(): void {
    this.showSubTopicModal = false;
    this.editingSubTopic = null;
    this.subTopicForm.reset();
  }

  saveSubTopicModal(): void {
    if (this.subTopicForm.valid) {
      const formData = this.subTopicForm.value;
      
      if (this.modalMode === 'add') {
        this.addSubTopicLocally(formData);
      } else if (this.editingSubTopic) {
        this.updateSubTopicLocally(this.editingSubTopic, formData);
      }
      
      this.closeSubTopicModal();
    } else {
      this.markFormGroupTouched(this.subTopicForm);
    }
  }

  deleteSubTopic(subTopic: LocalSubTopic): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Are you sure you want to delete the subtopic "${subTopic.sub_topic_name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteSubTopicLocally(subTopic);
      }
    });
  }

  // Local State Management Methods
  private addTopicLocally(topicData: any): void {
    const tempId = new Date().getTime();
    const newTopic: LocalTopic = {
      topic_id: tempId,
      tempId: tempId,
      site_id: this.siteId,
      topic_name: topicData.topic_name,
      is_system: false,
      status: true,
      created_date: new Date().toISOString(),
      updated_on: new Date().toISOString(),
      sub_topics: [],
      action: 'create'
    };

    this.topics.push(newTopic);
    this.selectedTopic = newTopic;
    this.selectedSubTopics = [];
    this.refreshVisibleLists();
    this.hasChanges = true;
  }

  private updateTopicLocally(topic: LocalTopic, updatedData: any): void {
    const index = this.topics.findIndex(t => t.topic_id === topic.topic_id);
    if (index !== -1) {
      // If it's a newly created topic, keep action as 'create'
      if (topic.action !== 'create') {
        topic.action = 'update';
      }
      
      topic.topic_name = updatedData.topic_name;
      topic.updated_on = new Date().toISOString();
      this.refreshVisibleLists();
      this.hasChanges = true;
    }
  }

  private deleteTopicLocally(topic: LocalTopic): void {
    const index = this.topics.findIndex(t => t.topic_id === topic.topic_id);
    if (index !== -1) {
      if (topic.action === 'create') {
        // Remove completely if it was locally created
        this.topics.splice(index, 1);
      } else {
        // Mark for deletion if it exists on server
        topic.action = 'delete';
      }
      
      // Update selection if needed
      if (this.selectedTopic?.topic_id === topic.topic_id) {
        this.selectedTopic = this.topics.length > 0 ? this.topics[0] : null;
        this.selectedSubTopics = this.selectedTopic?.sub_topics as LocalSubTopic[] || [];
      }
      this.refreshVisibleLists();
      this.hasChanges = true;
    }
  }

  private addSubTopicLocally(subTopicData: any): void {
    // Find the target topic
    const targetTopic = this.topics.find(t => t.topic_id === subTopicData.topic_id);
    if (!targetTopic) {
      this.showError('Selected topic not found');
      return;
    }

    const tempId = new Date().getTime();
    const newSubTopic: LocalSubTopic = {
      sub_topic_id: tempId,
      tempId: tempId,
      site_id: this.siteId,
      topic_id: subTopicData.topic_id,
      sub_topic_name: subTopicData.sub_topic_name,
      secondary_prompt: subTopicData.secondary_prompt,
      is_system: false,
      status: true,
      created_date: new Date().toISOString(),
      updated_on: new Date().toISOString(),
      action: 'create'
    };

    if (!targetTopic.sub_topics) {
      targetTopic.sub_topics = [];
    }
    
    (targetTopic.sub_topics as LocalSubTopic[]).push(newSubTopic);
    
    // Update selected subtopics if this is the currently selected topic
    if (this.selectedTopic?.topic_id === targetTopic.topic_id) {
      this.selectedSubTopics = targetTopic.sub_topics as LocalSubTopic[];
    }
    this.refreshVisibleLists();
    this.hasChanges = true;
  }

  private updateSubTopicLocally(subTopic: LocalSubTopic, updatedData: any): void {
    // If topic changed, we need to move the subtopic
    if (subTopic.topic_id !== updatedData.topic_id) {
      // Remove from old topic
      const oldTopic = this.topics.find(t => t.topic_id === subTopic.topic_id);
      if (oldTopic && oldTopic.sub_topics) {
        const index = (oldTopic.sub_topics as LocalSubTopic[]).findIndex(st => st.sub_topic_id === subTopic.sub_topic_id);
        if (index !== -1) {
          (oldTopic.sub_topics as LocalSubTopic[]).splice(index, 1);
        }
      }

      // Add to new topic
      const newTopic = this.topics.find(t => t.topic_id === updatedData.topic_id);
      if (newTopic) {
        if (!newTopic.sub_topics) {
          newTopic.sub_topics = [];
        }
        subTopic.topic_id = updatedData.topic_id;
        (newTopic.sub_topics as LocalSubTopic[]).push(subTopic);
      }

      // Update selected subtopics if needed
      if (this.selectedTopic?.topic_id === updatedData.topic_id) {
        this.selectedSubTopics = newTopic?.sub_topics as LocalSubTopic[] || [];
      } else if (this.selectedTopic?.topic_id === oldTopic?.topic_id) {
        this.selectedSubTopics = oldTopic?.sub_topics as LocalSubTopic[] || [];
      }
    }

    // If it's a newly created subtopic, keep action as 'create'
    if (subTopic.action !== 'create') {
      subTopic.action = 'update';
    }
    
    subTopic.sub_topic_name = updatedData.sub_topic_name;
    subTopic.secondary_prompt = updatedData.secondary_prompt;
    subTopic.updated_on = new Date().toISOString();
    this.refreshVisibleLists();
    this.hasChanges = true;
  }

  private deleteSubTopicLocally(subTopic: LocalSubTopic): void {
    if (!this.selectedTopic) return;

    const subTopics = this.selectedTopic.sub_topics as LocalSubTopic[];
    const index = subTopics.findIndex(st => st.sub_topic_id === subTopic.sub_topic_id);
    
    if (index !== -1) {
      if (subTopic.action === 'create') {
        // Remove completely if it was locally created
        subTopics.splice(index, 1);
      } else {
        // Mark for deletion if it exists on server
        subTopic.action = 'delete';
      }
      
      this.selectedSubTopics = subTopics;
      this.refreshVisibleLists();
      this.hasChanges = true;
    }
  }

  // Save all changes using batch operations
  saveAllChanges(): void {
    if (!this.hasChanges) {
      console.log('No changes to save in AI Topic Manager');
      return;
    }

    this.saving = true;
    const payload = this.buildBatchPayload();

    this.aiTopicService.batchOperations(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.showSuccess('All changes saved successfully');
          this.hasChanges = false;
          this.loadTopics(); // Reload to get updated data from server
        } else {
          this.showError('Failed to save changes: ' + response.message);
        }
        this.saving = false;
      },
      error: (error) => {
        console.error('Error saving changes:', error);
        this.showError('Error saving changes. Please try again.');
        this.saving = false;
      }
    });
  }

  private buildBatchPayload(): BatchOperationsPayload {
    const payload: BatchOperationsPayload = {
      site_id: this.siteId,
      operations: {}
    };

    // Collect delete operations
    const deleteTopics: number[] = [];
    const deleteSubTopics: number[] = [];

    // Collect update operations
    const updateTopics: any[] = [];
    const updateSubTopics: any[] = [];

    // Collect create operations
    const createTopics: any[] = [];
    const createSubTopics: any[] = [];

    this.topics.forEach(topic => {
      if (topic.action === 'delete' && !topic.tempId) {
        deleteTopics.push(topic.topic_id);
      } else if (topic.action === 'update') {
        updateTopics.push({
          topic_id: topic.topic_id,
          topic_name: topic.topic_name
        });
      } else if (topic.action === 'create') {
        const subTopicsToCreate = (topic.sub_topics as LocalSubTopic[] || [])
          .filter(st => st.action === 'create')
          .map(st => ({
            sub_topic_name: st.sub_topic_name,
            secondary_prompt: st.secondary_prompt
          }));

        createTopics.push({
          topic_name: topic.topic_name,
          sub_topics: subTopicsToCreate
        });
      }

      // Handle subtopic operations for existing topics
      if (topic.action !== 'create' && topic.action !== 'delete') {
        (topic.sub_topics as LocalSubTopic[] || []).forEach(subTopic => {
          if (subTopic.action === 'delete' && !subTopic.tempId) {
            deleteSubTopics.push(subTopic.sub_topic_id);
          } else if (subTopic.action === 'update') {
            updateSubTopics.push({
              sub_topic_id: subTopic.sub_topic_id,
              sub_topic_name: subTopic.sub_topic_name,
              secondary_prompt: subTopic.secondary_prompt
            });
          } else if (subTopic.action === 'create') {
            // Handle standalone subtopic creation for existing topics
            createSubTopics.push({
              topic_id: topic.topic_id,
              sub_topic_name: subTopic.sub_topic_name,
              secondary_prompt: subTopic.secondary_prompt
            });
          }
        });
      }
    });

    // Build operations object
    if (deleteTopics.length > 0 || deleteSubTopics.length > 0) {
      payload.operations.delete = {};
      if (deleteTopics.length > 0) payload.operations.delete.topics = deleteTopics;
      if (deleteSubTopics.length > 0) payload.operations.delete.subTopics = deleteSubTopics;
    }

    if (updateTopics.length > 0 || updateSubTopics.length > 0) {
      payload.operations.update = {};
      if (updateTopics.length > 0) payload.operations.update.topics = updateTopics;
      if (updateSubTopics.length > 0) payload.operations.update.subTopics = updateSubTopics;
    }

    if (createTopics.length > 0 || createSubTopics.length > 0) {
      payload.operations.create = {};
      if (createTopics.length > 0) payload.operations.create.topics = createTopics;
      if (createSubTopics.length > 0) payload.operations.create.subTopics = createSubTopics;
    }

    return payload;
  }

  // Reset all local changes
  resetChanges(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Reset',
        message: 'Are you sure you want to discard all local changes? This action cannot be undone.',
        confirmText: 'Reset',
        cancelText: 'Cancel'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.hasChanges = false;
        this.loadTopics();
        this.showSuccess('All changes have been reset');
      }
    });
  }

  // Get filtered topics for display (exclude deleted ones)
  getVisibleTopics(): LocalTopic[] {
    return this.visibleTopics;
  }

  // Get filtered subtopics for display (exclude deleted ones)
  getVisibleSubTopics(): LocalSubTopic[] {
    return this.visibleSubTopics;
  }

  trackByTopicId(_: number, topic: LocalTopic): number {
    return topic.topic_id;
  }

  trackBySubTopicId(_: number, subTopic: LocalSubTopic): number {
    return subTopic.sub_topic_id;
  }

  private refreshVisibleLists(): void {
    this.visibleTopics = this.topics.filter(topic => topic.action !== 'delete');
    this.visibleSubTopics = this.selectedSubTopics.filter(subTopic => subTopic.action !== 'delete');
  }

  // Get action badge class for styling
  getActionBadgeClass(action: string): string {
    switch (action) {
      case 'create': return 'badge-create';
      case 'update': return 'badge-update';
      case 'delete': return 'badge-delete';
      default: return 'badge-same';
    }
  }

  // Get subtopic count helper
  getSubTopicCount(topic: LocalTopic): number {
    return (topic.sub_topics as LocalSubTopic[] || []).filter(st => st.action !== 'delete').length;
  }

  // Form validation helper
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  getFieldError(fieldName: string, formGroup: FormGroup): string {
    const field = formGroup.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.replace('_', ' ')} is required`;
      }
      if (field.errors['minlength']) {
        return `${fieldName.replace('_', ' ')} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  private showSuccess(message: string): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: message
    });
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message
    });
  }

  private showInfo(message: string): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: message
    });
  }
}
